import { RenderEngine } from "./RenderEngine";
import { Raycaster, Vector2, Intersection, Object3D } from "three";
import { ConfiguratorModel, Seat, SelectionGroup } from "./ConfiguratorModel";
import {Controller3D} from "./3DController";
import { CarModelCameraList, CarModelConfig, ModelLoadController } from "./ModelLoad/ModelLoadController";
import { DrivingAnimationController } from "./DrivingAnimationController";
import { TrunkCargo, TrunkLoadingController } from "./TrunkLoadingController";
import { GenericPassengerController } from "./GenericPassengerController";
import { XRayController } from "./XRayController";
import { XREngine } from "../ARController/XREngine";
import { Hotspot3D } from "./Hotspot3D";
import { SequenceController } from "./SequenceController";

interface IGroundHotspot {
  trigger:string;
  object:string;
  camera:CarModelCameraList;
}

const ghsPlayIcon = "GHS_Play_Icon";
const ghsPlayTrigger = "GHS_Play_Trigger";

/**
 * A controller that handles selecting objects in the 3D scene.
 */
export class SelectionController {
  private renderEngine:RenderEngine;
  private raycaster:Raycaster = new Raycaster();
  private mouse:Vector2 = new Vector2();

  // private state:State = State.Idle;
  // private highlightObject:Object3D = null;
  // private selectionObject:Object3D = null;
  
  private carpaintObjects:string[] = [];
  private interiorColorObjects:string[] = [];
  private lightObjects:string[] = [];
  private wheelObjects:string[] = [];
  
  private doorFrontLeftObjects:string[] = [];
  private doorFrontRightObjects:string[] = [];
  private doorRearLeftObjects:string[] = [];
  private doorRearRightObjects:string[] = [];
  private trunkLidObjects:string[] = [];
  
  private seatFrontLeftObjects:string[] = [];
  private seatFrontRightObjects:string[] = [];
  private seatRearLeftObjects:string[] = [];
  private seatRearRightObjects:string[] = [];
  private seatObjects:string[] = [];
  private sunRoofObjects:string[] = [];
  private welcomeScreenObject:string;
  private cupHolderObject:string;
  private exitInteriorObjects:string[];
  private groundHotspots:IGroundHotspot[] = [];
  private plateObjects:string[];

  private selectableObjects:string[] = [];
  private hiddenTriggerObjects:string[] = [];

  // Whether hotspots (in general) are hidden or visible.
  private hotspotsVisible:boolean = false;

  // Objects that show a "pointer" cursor when hovered.
  private pointerObjects:string[] = [];

  // TODO: probably best to move the configuration part somewhere else.
  private modelController:ConfiguratorModel;
  private controller3D:Controller3D;
  private trunkLoadingController:TrunkLoadingController;
  private genericPassengerController:GenericPassengerController;
  private xrayController:XRayController;
  private sequenceController:SequenceController;

  // Used to distinguish mouse dragging and clicking.
  private detectedMouseDown:boolean = false;
  private detectedMouseDownTime:number;
  private detectedMouseDownXY:{x:number, y:number} = {x: 0, y:0};

  // Do not raycast mouse position just all the time.
  private mouseMoveSkipCounter:number = 0;

  private showDisclaimerCB:()=>void = () => {};

  private enableRaycastLog:boolean = false;

  public constructor(renderEngine:RenderEngine,
                     isMobile:boolean) {
    this.renderEngine = renderEngine;
    (window as any)["SelectionController"] = this;
  }

  public initialize(
    modelController:ConfiguratorModel,
    controller3D:Controller3D,
    trunkLoadingController:TrunkLoadingController,
    genericPassengerController:GenericPassengerController,
    xrayController:XRayController,
    sequenceController:SequenceController
  ):void {
    this.modelController = modelController;
    this.controller3D = controller3D;
    this.trunkLoadingController = trunkLoadingController;
    this.genericPassengerController = genericPassengerController;
    this.xrayController = xrayController;
    this.sequenceController = sequenceController;

    ConfiguratorModel.event.addListener('interior', () => {
      // Do not show the interaction pointer e.g. on the skydome,
      // while we are not in the interior and it can't perform any action.
      if (this.controller3D.isInterior()) {
        for (const name of this.exitInteriorObjects) {
          if (this.pointerObjects.indexOf(name) == -1)
            this.pointerObjects.push(name);
        }
      } else {
        for (const name of this.exitInteriorObjects) {
          this.pointerObjects = this.pointerObjects.filter(name2 => name !== name2);
        }
      }

      this.updateGroundHotspotVisibility();
    });

    // Make sure that ground hotspots get hidden during WebXR sessions.
    this.controller3D.arController.xrEngine.event.on(XREngine.EVENT_STATUS_CHANGE, this.updateGroundHotspotVisibility);

    ModelLoadController.event.addListener(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this.updateObjectLists(config);
      this.updateGroundHotspotVisibility();
    });

    DrivingAnimationController.event.addListener(DrivingAnimationController.ENABLE_CHANGE, () => {
      this.updateGroundHotspotVisibility();
    })

    this.initializeLinearAccelerationSensor();

    (window as any)["enable_raycast_log"] = () => {
      this.enableRaycastLog = true;
    };
  }

  public exitGroundHotspot():void {
    if (this.controller3D.isGroundHotspot()) {
      this.controller3D.setCamera(CarModelCameraList.Orbit);
      this.updateGroundHotspotVisibility();
    }
  }

  private updateObjectLists(config:CarModelConfig):void {
    // TODO: store config object directly?
    // This is kind of pointless...
    this.carpaintObjects = config.trigger.carpaint || [];
    this.interiorColorObjects = config.trigger.interiorColor || [];
    this.wheelObjects = config.trigger.wheels || [];
    this.lightObjects = config.trigger.lights || [];
    this.doorFrontLeftObjects = config.trigger.doorFL || [];
    this.doorFrontRightObjects = config.trigger.doorFR || [];
    this.doorRearLeftObjects = config.trigger.doorRL || [];
    this.doorRearRightObjects = config.trigger.doorRR || [];
    this.trunkLidObjects = config.trigger.trunkLid || [];

    this.seatFrontLeftObjects = config.trigger.seatFL || [];
    this.seatFrontRightObjects = config.trigger.seatFR || [];
    this.seatRearLeftObjects = config.trigger.seatRL || [];
    this.seatRearRightObjects = config.trigger.seatRR || [];

    const doorObjects = [
      this.doorFrontLeftObjects,
      this.doorFrontRightObjects,
      this.doorRearLeftObjects,
      this.doorRearRightObjects,
      this.trunkLidObjects
    ].flat();

    this.seatObjects = [
      this.seatFrontLeftObjects,
      this.seatFrontRightObjects,
      this.seatRearLeftObjects,
      this.seatRearRightObjects,
    ].flat();

    this.sunRoofObjects = config.trigger.sunRoof || [];
    this.welcomeScreenObject = config.trigger.welcomeScreen || "";
    this.cupHolderObject = config.trigger.cupHolder || "";
    this.exitInteriorObjects = config.trigger.exitInterior || [];

    this.groundHotspots = [
      {
        trigger: config.trigger.ghLights.trigger,
        object: config.trigger.ghLights.object,
        camera: CarModelCameraList.GroundHotspotLights
      },
      {
        trigger:config.trigger.ghDoorsLeft.trigger,
        object: config.trigger.ghDoorsLeft.object,
        camera: CarModelCameraList.GroundHotspotDoorsLeft
      },
      {
        trigger: config.trigger.ghTrunk.trigger,
        object: config.trigger.ghTrunk.object,
        camera: CarModelCameraList.GroundHotspotTrunk
      },
      {
        trigger:config.trigger.ghDoorsRight.trigger,
        object: config.trigger.ghDoorsRight.object,
        camera: CarModelCameraList.GroundHotspotDoorsRight
      }
    ];

    this.plateObjects = config.trigger.plate;

    this.hiddenTriggerObjects = [
      doorObjects,
      this.seatObjects,
      this.sunRoofObjects,
      this.welcomeScreenObject,
      this.cupHolderObject,
      this.plateObjects,
      this.interiorColorObjects
    ].flat();

    this.selectableObjects = [
      this.carpaintObjects,
      this.interiorColorObjects,
      this.lightObjects,
      this.wheelObjects,
      doorObjects,
      this.seatObjects,
      this.groundHotspots.map(hotspot => hotspot.trigger),
      this.sunRoofObjects,
      this.welcomeScreenObject,
      this.cupHolderObject,
      this.exitInteriorObjects,
      this.plateObjects,
      ghsPlayTrigger
    ].flat();

    this.pointerObjects = [
      this.interiorColorObjects,
      this.lightObjects,
      this.wheelObjects,
      doorObjects,
      this.seatObjects,
      this.sunRoofObjects,
      this.welcomeScreenObject,
      this.cupHolderObject,
      this.groundHotspots.map(hotspot => hotspot.trigger),
      this.plateObjects,
      ghsPlayTrigger
    ].flat();
  }

  public hideGroundHotspots():void {
    // if (this.controller3D && this.controller3D.isGroundHotspot())
    //   this.exitGroundHotspot();

    this.hotspotsVisible = false;
    this.updateGroundHotspotVisibility();
  }

  public showGroundHotspots():void {
    this.hotspotsVisible = true;
    this.updateGroundHotspotVisibility();
  }

  public groundHotspotRotateLeft():void {
    for (let i = 0; i < this.groundHotspots.length; i++) {
      const groundHotspot = this.groundHotspots[i];
      if (this.controller3D.currentCamMatchesKey(groundHotspot.camera)) {
        const j = (i - 1 + this.groundHotspots.length) % this.groundHotspots.length;
        this.gotoGroundHotspot(this.groundHotspots[j]);
        break;
      }
    }
  }

  public groundHotspotRotateRight():void {
    for (let i = 0; i < this.groundHotspots.length; i++) {
      const groundHotspot = this.groundHotspots[i];
      if (this.controller3D.currentCamMatchesKey(groundHotspot.camera)) {
        const j = (i + 1) % this.groundHotspots.length;
        this.gotoGroundHotspot(this.groundHotspots[j]);
        break;
      }
    }
  }

  public onClick = (event:React.MouseEvent<HTMLDivElement, MouseEvent>):void => {
    // Distinguish mouse dragging from genuine click.
    const dx = event.clientX - this.detectedMouseDownXY.x;
    const dy = event.clientY - this.detectedMouseDownXY.y;
    if (this.detectedMouseDown) {
      const deltaTime = (performance.now() - this.detectedMouseDownTime);
      const deltaXY = dx * dx + dy * dy;
      // const driving = this.modelController.drivingAnimationController.animating;
      const driving = false;
      
      this.detectedMouseDown = false;
      
      if ((deltaTime > 500 || deltaXY >= 25) && !driving) 
        return;
    }

    const intersection = this.intersectMouseWithScene(event);

    // if (intersections.length == 0 || this.state == State.Blocked) {
      // return;
    // }

    if (!intersection) {
      return;
    }

    // this.renderEngine.outlinePass.selectedObjects = [object];
    // this.renderEngine.outlinePass.selectedEdgeAlpha = 0;

    this.select(intersection);

    // if (this.animationId != null) {
    //   clearInterval(this.animationId);
    // }

    // // Selection fade-out animation
    // let x = 0.0;
    // this.animationId = setInterval(() => {
    //   x += 0.0125;
    //   if (x >= 1) {
    //     x = 1;
    //     clearInterval(this.animationId);
    //     this.animationId = null;
    //     this.renderEngine.outlinePass.selectedObjects = [];
    //   }

    //   let y;
    //   if (x >= 0.1) {
    //     y = 1 - (x - 0.1) / 0.9;
    //   } else {
    //     y = x * 10.0;
    //   }
    //   this.renderEngine.outlinePass.selectedEdgeAlpha = y;
    // }, 16);
  }

  public onMouseMove = (event:React.MouseEvent<HTMLDivElement, MouseEvent>):void => {
    if (this.mouseMoveSkipCounter++ != 10) {
      return;
    }
    this.mouseMoveSkipCounter = 0;

    if (this.detectedMouseDown) {
      return;
    }

    const intersection = this.intersectMouseWithScene(event);

    let isPointerObject = intersection && this.pointerObjects.indexOf(intersection.object.name) != -1;

    isPointerObject ||= intersection && this.trunkLoadingController.isHiddenTrigger(intersection.object.name);

    if (intersection && isPointerObject) {
      document.body.style.cursor = "pointer";
    } else {
      document.body.style.cursor = "inherit";
    }
  }

  public onMouseDown = (event:React.MouseEvent<HTMLDivElement, MouseEvent>):void => {
    this.detectedMouseDown = true;
    this.detectedMouseDownTime = performance.now();
    this.detectedMouseDownXY = {
      x: event.clientX,
      y: event.clientY
    }
  }

  public setCloseMenuCallback(closeMenuCB:()=>void) {
    // not used anymore, just kept there as to not break code...
  }

  public setShowDisclaimerCallback(showDisclaimerCB:()=>void):void {
    this.showDisclaimerCB = showDisclaimerCB;
  }

  private intersectMouseWithScene(event:React.MouseEvent<HTMLDivElement, MouseEvent>):Intersection {
    // TODO: this assumes that the canvas always fills the app container.
    // This was preferred over using the engine canvas as it works in WebXR.
    const contentDiv = document.getElementById("app-include-content");
    const clientRect = contentDiv.getBoundingClientRect();
    this.mouse.x =   ((event.clientX - clientRect.left) / contentDiv.clientWidth ) * 2 - 1;
    this.mouse.y = -(((event.clientY - clientRect.top ) / contentDiv.clientHeight) * 2 - 1);

    // Make sure that we use the scene and camera that we're actually rendering with.
    // This is crucial when we render in WebXR.
    const sceneRenderer = (<any>this.renderEngine.engine.services.render.renderController.composer).sceneRenderer;
    const scene = sceneRenderer.scene;
    const camera = sceneRenderer.camera;
  
    if (!scene) {
      return null;
    }

    this.raycaster.setFromCamera(this.mouse, camera);

    const hotspotScene = this.renderEngine.hotspotRenderPass.hotspotScene;
    if (hotspotScene.visible) {
      const hotspotIntersections = this.raycaster.intersectObjects(
        hotspotScene.children, true
      ).filter(intersection => intersection.object.parent.visible && (<Hotspot3D>intersection.object.parent).onClick !== undefined);
      if (hotspotIntersections.length > 0) {
        return hotspotIntersections[0];
      }
    }

    let intersections = this.raycaster.intersectObjects(
      scene.children, true
    );

    if (this.enableRaycastLog && intersections[0]) {
      console.log("raycast", intersections[0].object.name);
    }

    intersections = intersections.filter(
      intersection => 
        intersection.object.visible ||
        this.hiddenTriggerObjects.indexOf(intersection.object.name) != -1 ||
        this.trunkLoadingController.isHiddenTrigger(intersection.object.name)
    );

    const intersection = intersections[0];

    if (intersection) {
      let isSelectableObject = this.selectableObjects.indexOf(intersection.object.name) != -1;

      isSelectableObject ||= this.trunkLoadingController.isHiddenTrigger(intersection.object.name);
      isSelectableObject ||= intersection.object.parent && (intersection.object.parent instanceof Hotspot3D);

      if (isSelectableObject) {
        return intersection;
      }
    }

    return null;
  }

  private select(intersection:Intersection):void {
    const object = intersection.object;
    
    if (this.sequenceController.running) {
      return;
    }

    if (object.parent && object.parent instanceof Hotspot3D) {
      object.parent.onClick();
    }

    if (this.trunkLoadingController.onSelection(intersection.object.name)) {
      return;
    }

    if (this.trunkLoadingController.active) {
      // TODO: deduplicate this piece of code...
      if (!this.trunkLoadingController.blocked && this.trunkLidObjects.indexOf(object.name) != -1) {
        this.controller3D.playTrunkLidAnimation();
        return;
      }
      return;
    }

    if (this.genericPassengerController.active) {
      if (this.doorFrontLeftObjects.indexOf(object.name) != -1) {
        this.genericPassengerController.changeLocation(1, true);
        return;
      }

      if (this.doorRearLeftObjects.indexOf(object.name) != -1) {
        this.genericPassengerController.changeLocation(2, true);
        return;
      }

      if (this.doorRearRightObjects.indexOf(object.name) != -1) {
        this.genericPassengerController.changeLocation(3, true);
        return;
      }

      if (this.doorFrontRightObjects.indexOf(object.name) != -1) {
        this.genericPassengerController.changeLocation(4, true);
        return;
      }
      return;
    }

    if (this.xrayController.active) {
      return;
    }

    const xrActive = this.controller3D.arController.xrEngine.active;

    if (!this.modelController.drivingAnimationController.animating) {
      if (!xrActive) {
        for (const groundHotspot of this.groundHotspots) {
          if (object.name === groundHotspot.trigger) {
            this.gotoGroundHotspot(groundHotspot);
            return;
          }
        }
      }

      if (object.name === ghsPlayTrigger) {
        this.sequenceController.playHighlightFly();
        return;
      }
  
      if (this.plateObjects.indexOf(object.name) != -1) {
        this.showDisclaimerCB();
        return;
      }
  
      if (!xrActive) {
        if (this.seatFrontLeftObjects.indexOf(object.name) != -1) {
          if (!this.controller3D.isInterior()) {
            this.modelController.setInterior(true, Seat.FrontLeft, true);
            return;
          }
          this.modelController.seatSelectionGroup.changeSelection(this.modelController.seatSelectionGroup.selections[0].id, true, true);
          return;
        }
    
        if (this.seatFrontRightObjects.indexOf(object.name) != -1) {
          if (!this.controller3D.isInterior()) {
            this.modelController.setInterior(true, Seat.FrontRight, true);
            return;
          }
          this.modelController.seatSelectionGroup.changeSelection(this.modelController.seatSelectionGroup.selections[1].id, true, true);
          return;
        }
    
        if (this.seatRearLeftObjects.indexOf(object.name) != -1) {
          if (!this.controller3D.isInterior()) {
            this.modelController.setInterior(true, Seat.RearLeft, true);
            return;
          }
          this.modelController.seatSelectionGroup.changeSelection(this.modelController.seatSelectionGroup.selections[3].id, true, true);
          return;
        }
    
        if (this.seatRearRightObjects.indexOf(object.name) != -1) {
          if (!this.controller3D.isInterior()) {
            this.modelController.setInterior(true, Seat.RearRight, true);
            return;
          }
          this.modelController.seatSelectionGroup.changeSelection(this.modelController.seatSelectionGroup.selections[2].id, true, true);
          return;
        }
      }

      if (this.doorFrontLeftObjects.indexOf(object.name) != -1) {
        this.controller3D.playFrontLeftDoorAnimation(true, true);
        return;
      }
  
      if (this.doorFrontRightObjects.indexOf(object.name) != -1) {
        this.controller3D.playFrontRightDoorAnimation(true, true);
        return;
      }
  
      if (this.doorRearLeftObjects.indexOf(object.name) != -1) {
        this.controller3D.playRearLeftDoorAnimation(true, true);
        return;
      }
  
      if (this.doorRearRightObjects.indexOf(object.name) != -1) {
        this.controller3D.playRearRightDoorAnimation(true, true);
        return;
      }
  
      if (this.trunkLidObjects.indexOf(object.name) != -1) {
        this.controller3D.playTrunkLidAnimation(true, true);
        return;
      }
    }

    if (object.name === this.welcomeScreenObject) {
      this.modelController.turnWelcomeScreen();
      return;
    }

    if (object.name === this.cupHolderObject) {
      this.controller3D.playCupHolderAnimation(true);
      return;
    }

    if (!xrActive) {
      if (this.controller3D.isInterior() && this.exitInteriorObjects.indexOf(object.name) != -1) {
        this.modelController.setInterior(false, Seat.FrontLeft, true);
        return;
      }
    }

    if (this.sunRoofObjects.indexOf(object.name) != -1) {
      this.controller3D.playSunRoofAnimation(true, true);
      return;
    }

    if (this.carpaintObjects.indexOf(object.name) != -1) {
      this.selectionGroupRotateRight(this.modelController.carpaintSelectionGroup);
      return;
    }

    if (this.interiorColorObjects.indexOf(object.name) != -1) {
      this.selectionGroupRotateRight(this.modelController.interiorSelectionGroup);
      return;
    }

    if (this.lightObjects.indexOf(object.name) != -1) {
      this.modelController.turnLight(true, true);
      return;
    }

    if (this.wheelObjects.indexOf(object.name) != -1) {
      this.selectionGroupRotateRight(this.modelController.wheelSelectionGroup);
      return;
    }
  }

  private selectionGroupRotateRight(selectionGroup:SelectionGroup):void {
    const selections = selectionGroup.selections;
    const current = selectionGroup.currentSelection;
      
    // Search for the next applicable material
    for (let i = 1; i < selections.length; i++) {
      const next = (current + i) % selections.length;
      // TODO: for some reason calling 'changeSelection' on the SelectionGroup directly
      // causes issues with the exception system.
      this.modelController.changeSelection(selectionGroup.groupID, selections[next].id, true, true)
      // If the selection could be applied then stop.
      if (selectionGroup.currentSelection == next)
        break;
    }
  }

  private updateGroundHotspotVisibility = ():void => {
    if (!this.controller3D) {
      return;
    }

    const visible = this.hotspotsVisible &&
      !this.controller3D.isInterior() &&
      !this.controller3D.isGroundHotspot() &&
      !this.controller3D.arController.xrEngine.active && 
      !this.modelController.drivingAnimationController.animating;

    const scene = this.renderEngine.scene;

    for (const groundHotspot of this.groundHotspots) {
      const object = scene.getObjectByName(groundHotspot.object);
      if (object) {
        object.visible = visible;
        if (visible) {
          this.hiddenTriggerObjects.push(groundHotspot.trigger);
        } else {
          this.hiddenTriggerObjects = this.hiddenTriggerObjects.filter(o => o !== groundHotspot.trigger);
        }
      }
    }

    const object = scene.getObjectByName(ghsPlayIcon);
    if (object) {
      object.visible = visible;
      if (visible) {
        this.hiddenTriggerObjects.push(ghsPlayTrigger);
      } else {
        this.hiddenTriggerObjects = this.hiddenTriggerObjects.filter(o => o !== ghsPlayTrigger);
      }
    }
  }

  private gotoGroundHotspot(groundHotspot:IGroundHotspot) {
    // this.controller3D.setCamera(groundHotspot.camera, 1000);
    this.controller3D.enterGroundHotspot(groundHotspot.camera, 1000);

    // const object = this.renderEngine.scene.getObjectByName(groundHotspot.object);
    const objects = this.groundHotspots.map(g => this.renderEngine.scene.getObjectByName(g.object));
    const timer = setInterval(() => {
      for (const object of objects) {
        object.scale.x -= 0.1;
        object.scale.z -= 0.1;
        if (object.scale.x <= 0) {
          clearInterval(timer);
          this.updateGroundHotspotVisibility();
          object.scale.x = 1;
          object.scale.z = 1;
        }
      }
    }, 16);
  }

  private initializeLinearAccelerationSensor():void {
    if ('LinearAccelerationSensor' in window) {
      try {
        const sensor = new LinearAccelerationSensor({frequency: 10});
        
        sensor.onerror = (e) => {
          if (e.error) {
            throw e.error;
          }
          return;
        };

        let oldX = undefined;
        let oldY = undefined;
        let oldZ = undefined;

        let enabled = true;

        sensor.onreading = (e) => {
          const x = sensor.x;
          const y = sensor.y;
          const z = sensor.z;

          const threshold = 15;

          if (oldX !== undefined) {
            const deltaX = x - oldX;
            const deltaY = y - oldY;
            const deltaZ = z - oldZ;
          
            if (enabled && (deltaX > threshold || deltaY > threshold || deltaZ > threshold)) {
              enabled = false;
              setTimeout(() => enabled = true, 500);

              if (this.modelController.carpaintSelectionGroup) {
                this.selectionGroupRotateRight(this.modelController.carpaintSelectionGroup);
              }
            }
          }

          oldX = x;
          oldY = y;
          oldZ = z;
        };

        sensor.start();
      } catch (error) {
        console.error("Failed to initialized linear acceleration sensor", error);
      }
    }
  }
}